import React, { useContext } from 'react';

import {
	ApButtonSecondary,
	ApButtonMain,
	ApButtonGroup,
	ApCheckbox,
} from '@alixpartners/ui-components';
import moment from 'moment';
import { StepHeader } from './StepHeader';
import { InitiativeTypeForm } from './InitiativeTypeForm';
import { ItemsSelectionStep } from './ItemsSelectionStep';
import { PTItemsStep } from './PTItemsStep';
import { ConfigureInitiative } from './ConfigureInitiative';
import {
	getTailSpendAnalysisConfigurationForProcessing,
	getTailSpendAnalysisConfigurationForSave,
} from 'Settings/utils';
import {
	StyledModal,
	Content,
	ScrollableWrapper,
	CheckboxLabel,
	CheckboxWrapper,
	Title,
} from './InitiativeEditorModal.styles';
import {
	fetchWheelChartData,
	fetchTailSpendData,
	getProjectTaxonomies,
	getSettingsOptions,
	getWaves,
	createInitative,
	updateInitiative,
	getInitative,
	getOptimizationCategories,
	getHarmonizationVendors,
	getSpendCubeSettings,
	updateTailSpendSettings,
} from 'utils/apiRequests';
import { QueryStringContext } from 'contexts/QueryStringContext';
import { SettingsContext } from 'contexts/SettingsContext';
import { SettingsOptionsContext } from 'contexts/SettingsOptionsContext';
import { SpendCubeSettingsContext } from 'contexts/SpendCubeSettingsContext';
import { getCurrencySymbolFromOptions } from 'Details/utils';
import { SPEND_PARAM } from 'Data/constants';
import {
	STEPS_CONFIG,
	DEFAULT_PARAMS,
	ITEMS_TYPE,
	GROUP_OPTIONS,
	FILTER_OPTIONS,
	CATEGORY_PARAM,
	converDataToTree,
	convertInitiativeResp,
	convertPTImprovementResp,
	convertPTHarmonizationResp,
	getStartOfWeek,
	convertSaveData,
	getHierarchyFromTaxonomy,
	groupSegments,
	getLeaves,
} from '../utils';
import { getFormattedSpendWithSymbol } from 'utils/number';
import TailSpendSection from 'Settings/TailSpendSection';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';

const defaultSettings = {
	tailSpend: 20,
	tailSpendType: 0,
	useNetSpend: false,
};

export class InitiativeEditorModal extends React.Component {
	state = {
		activeStep: this.getDefaultActiveStep(),
		groupBy: GROUP_OPTIONS[1],
		filterCategory: FILTER_OPTIONS[0],
		filterPTCategory: null,
		data: null,
		taxonomies: null,
		defaultDateRange: null,
		dateRangeOptions: null,
		waves: null,
		isSaving: false,
		initiativeData: {
			name: 'Initiative name',
			executionStartDate: getStartOfWeek(),
			desription: '',
			duration: 1,
			waveId: undefined,
			wacc: 0,
			items: [],
			spendType: null,
			initiativeItemType: null,
			type: '',
			startDate: null,
			endDate: null,
			tailSegmentsConfiguration: null,
			taxonomyType: null,
		},
		isCommonVendors: false,
		isLoading: false,
		tailSpendConfig: null,
		settings: null,
		shouldUpdateSettings: false,
		showSegments: false,
	};

	componentDidMount() {
		const { id } = this.props;

		if (id) {
			getInitative(id).then(initiative => {
				this.changeInitiative(
					convertInitiativeResp(initiative),
					this.fetchData
				);
			});
		} else {
			this.fetchConfig();
		}

		this.fetchWaves();
	}

	async componentDidUpdate(_prevProps, prevState) {
		const {
			data,
			activeStep,
			initiativeData,
			filterCategory,
			groupBy,
			filterPTCategory,
			isCommonVendors,
			settings,
			tailSpendConfig,
		} = this.state;
		const { initiativeItemType, spendType, type } = initiativeData;

		if (!data && activeStep !== prevState.activeStep && activeStep === 2) {
			this.fetchData();
		}

		if (
			!settings &&
			activeStep !== prevState.activeStep &&
			activeStep === 4 &&
			initiativeItemType === ITEMS_TYPE.TAIL_SPEND
		) {
			this.fetchSettings();
		}

		if (!isEqual(tailSpendConfig, prevState.tailSpendConfig)) {
			this.updateInitiativeTailSegments();
		}

		if (
			(initiativeItemType &&
				initiativeItemType === ITEMS_TYPE.VENDOR &&
				(filterCategory !== prevState.filterCategory ||
					groupBy.value !== prevState.groupBy.value)) ||
			!isEqual(filterPTCategory, prevState.filterPTCategory)
		) {
			this.changeInitiative({ items: [] });
		}

		if (
			initiativeItemType !== prevState.initiativeData.initiativeItemType ||
			spendType !== prevState.initiativeData.spendType ||
			type !== prevState.initiativeData.type
		) {
			this.setState({ filterPTCategory: null });
		}

		if (
			initiativeItemType &&
			initiativeItemType === ITEMS_TYPE.PT_HARMONIZATION &&
			(!isEqual(filterPTCategory, prevState.filterPTCategory) ||
				isCommonVendors !== prevState.isCommonVendors)
		) {
			this.fetchPTHarmonizationData();
		}
	}

	getDefaultActiveStep() {
		const { id } = this.props;
		const index = id ? STEPS_CONFIG.length - 1 : 0;

		return STEPS_CONFIG[index].step;
	}

	updateInitiativeTailSegments = () => {
		const { initiativeData } = this.state;

		this.setState({
			initiativeData: {
				...initiativeData,
				tailSegmentsConfiguration: this.getTailSpendParams(),
				items: initiativeData.items.map(item =>
					omit(item, 'tailSegmentsLowHigh')
				),
			},
		});
	};

	getCategoryOptions() {
		const { data } = this.state;

		if (!data) {
			return FILTER_OPTIONS;
		}

		return [
			...FILTER_OPTIONS,
			...data.map(({ name, id }) => ({ label: name, value: id })),
		];
	}

	handleFilterCategoryChange = filterCategory => {
		this.setState({ filterCategory });
	};

	handleStepClick = activeStep => {
		this.setState({ activeStep });
	};

	toggleCommonVendors = () => {
		const { isCommonVendors } = this.state;

		this.setState({ isCommonVendors: !isCommonVendors });
	};

	getNextStep = () => {
		const { activeStep } = this.state;
		const stepsConfig = this.getStepsConfig();
		const stepItemIndex = stepsConfig.findIndex(
			item => item.step === activeStep
		);

		if (stepItemIndex === -1 || !stepsConfig[stepItemIndex + 1]) {
			return null;
		}

		return stepsConfig[stepItemIndex + 1].step;
	};

	handleGroupByChange = groupBy => {
		this.setState({ groupBy });
	};

	handleFilterPTCategoryChange = filterPTCategory => {
		this.setState({ filterPTCategory });
	};

	handleDataParamsChange = updates => {
		const { initiativeData } = this.state;

		this.setState({
			data: null,
			filterCategory: FILTER_OPTIONS[0],
			initiativeData: { ...initiativeData, items: [], ...updates },
		});
	};

	saveInitiative = () => {
		const { initiativeData, shouldUpdateSettings } = this.state;
		const { onClose, refetchList, id } = this.props;

		const request = id ? updateInitiative : createInitative;

		this.setState({ isSaving: true });
		request(convertSaveData(initiativeData))
			.then(() => {
				this.setState({ isSaving: false });

				if (
					initiativeData.initiativeItemType === ITEMS_TYPE.TAIL_SPEND &&
					shouldUpdateSettings
				) {
					updateTailSpendSettings(this.getTailSpendParams());
				}

				onClose();
				refetchList();
			})
			.catch(() => {
				this.setState({ isSaving: false });
			});
	};

	handleWaccChange = value => {
		const { id } = this.props;
		const { initiativeData, data } = this.state;
		const { items } = initiativeData;
		const isEdit = !!id;

		let number = Number(value);

		if (number > 100) {
			number = 100;
		}

		if (number < 0) {
			number = 0;
		}

		// Map default working capital impact
		// (edit data contains working capital impact already with wacc calculated)
		this.changeInitiative({
			wacc: number,
			...(isEdit
				? {
						items: items.map(item => {
							const defaultItem =
								data.find(
									taxonomy =>
										taxonomy.groupName === item.groupName &&
										taxonomy.name === item.name
								) || {};

							return {
								...item,
								workingCapital: defaultItem.workingCapitalImpact || 0,
							};
						}),
				  }
				: {}),
		});
	};

	handleSubmit = () => {
		const nextStep = this.getNextStep();

		if (nextStep) {
			this.setState({ activeStep: nextStep });
		} else {
			this.saveInitiative();
		}
	};

	changeInitiative = (updates, cb) => {
		const { initiativeData } = this.state;

		this.setState({ initiativeData: { ...initiativeData, ...updates } }, cb);
	};

	fetchData = () => {
		const { initiativeData } = this.state;
		const { initiativeItemType } = initiativeData;

		if (initiativeItemType === ITEMS_TYPE.PT_IMPROVEMENT) {
			this.fetchPTImprovementData();
		} else if (initiativeItemType === ITEMS_TYPE.PT_HARMONIZATION) {
			this.fetchPTHarmonizationData();
		} else if (initiativeItemType === ITEMS_TYPE.TAIL_SPEND) {
			this.fetchTailSpendData();
		} else {
			this.fetchSpendData();
		}

		this.fetchTaxonomies();
	};

	fetchSettings = () => {
		return getSpendCubeSettings().then(settings => {
			const { tailSpend, tailSpendAnalysisConfiguration } = settings;
			const tailSpendValue =
				tailSpend.value != null ? tailSpend.value : defaultSettings.tailSpend;
			const tailSpendType =
				tailSpend.type != null ? tailSpend.type : defaultSettings.tailSpendType;
			const useNetSpend =
				tailSpend.useNetSpend != null
					? tailSpend.useNetSpend
					: defaultSettings.useNetSpend;
			this.setState({
				settings,
				tailSpendConfig: {
					tailSpend: tailSpendValue,
					tailSpendType,
					useNetSpend,
					tailSpendAnalysisConfiguration:
						tailSpendAnalysisConfiguration &&
						getTailSpendAnalysisConfigurationForProcessing(
							tailSpendAnalysisConfiguration
						),
				},
			});
		});
	};

	fetchConfig = () => {
		const { settings } = this.props;

		return getSettingsOptions().then(configOptions => {
			const { initiativeData } = this.state;
			const defaultDateRange =
				settings && settings.initiativesDateRangeId
					? configOptions.dateRangesForInitiatives.find(
							item => item.id === settings.initiativesDateRangeId
					  ) || configOptions.dateRangesForInitiatives[0]
					: configOptions.dateRangesForInitiatives[0];

			this.setState({
				defaultDateRange,
				dateRangeOptions: configOptions.dateRangesForInitiatives,
				initiativeData: {
					...initiativeData,
					startDate: defaultDateRange.dateFrom,
					endDate: defaultDateRange.dateTo,
				},
			});
		});
	};

	fetchWaves = () => {
		return getWaves().then(waves => {
			this.setState({
				waves,
			});
		});
	};

	fetchPTHarmonizationData = () => {
		const { initiativeData, filterPTCategory, isCommonVendors } = this.state;
		const { spendType, startDate, endDate, taxonomyType } = initiativeData;
		const hierarchy = getHierarchyFromTaxonomy(filterPTCategory);
		const params = {
			spendType,
			dateFrom: startDate,
			dateTo: endDate,
			hierarchy,
			taxonomyType,
			...(isCommonVendors ? { hasMultipleCompanies: true } : {}),
		};

		this.setState({ isLoading: true });

		getHarmonizationVendors(params).then(resp => {
			this.setState({
				isLoading: false,
				data: convertPTHarmonizationResp(resp),
			});
		});
	};

	fetchPTImprovementData = () => {
		const { initiativeData } = this.state;
		const { spendType, startDate, endDate, taxonomyType } = initiativeData;
		const params = {
			spendType,
			dateFrom: startDate,
			dateTo: endDate,
			taxonomyType,
		};

		return getOptimizationCategories(params).then(resp => {
			this.setState({
				taxonomies: [],
				data: convertPTImprovementResp(resp),
			});
		});
	};

	async fetchSpendData() {
		const { currency } = this.props;
		const { initiativeData } = this.state;
		const {
			spendType,
			initiativeItemType,
			startDate,
			endDate,
		} = initiativeData;
		const categoryDimensions = this.getCategoryDataDimensions();
		const apiRequestParams = {
			...DEFAULT_PARAMS,
			dimensions:
				initiativeItemType === ITEMS_TYPE.CATEGORY
					? categoryDimensions
					: [...categoryDimensions, 'harmonizedSupplier'],
			currency,
			filters: [...DEFAULT_PARAMS.filters, 'spendType'],
			filterValues: [...DEFAULT_PARAMS.filterValues, spendType],
			dateFrom: startDate,
			dateTo: endDate,
		};

		const { data } = await fetchWheelChartData(apiRequestParams);

		this.setState({ data: converDataToTree(data) });
	}

	getCategoryDataDimensions = () => {
		const { initiativeData } = this.state;
		const { taxonomyType } = initiativeData;
		const category = CATEGORY_PARAM[taxonomyType] || 'category';

		return Array(4).fill(category);
	};

	getTailSpendParams = () => {
		const { tailSpendConfig } = this.state;
		const segments =
			getTailSpendAnalysisConfigurationForSave(
				tailSpendConfig.tailSpendAnalysisConfiguration
			).segments || [];

		return {
			tailSpendValue: tailSpendConfig.tailSpend,
			tailSpendType: tailSpendConfig.tailSpendType,
			calculationType:
				tailSpendConfig.tailSpendAnalysisConfiguration.calculationType,
			segments,
		};
	};

	async fetchTailSpendData() {
		const { currency } = this.props;
		const { initiativeData } = this.state;
		const {
			spendType,
			startDate,
			endDate,
			tailSegmentsConfiguration,
		} = initiativeData;
		const apiRequestParams = {
			...DEFAULT_PARAMS,
			currency,
			dimensions: this.getCategoryDataDimensions(),
			filters: [...DEFAULT_PARAMS.filters, 'spendType'],
			filterValues: [...DEFAULT_PARAMS.filterValues, spendType],
			dateFrom: startDate,
			dateTo: endDate,
			disableTailSpendSegments: false,
			getTailSpend: true,
		};

		const { data } = await fetchTailSpendData(
			apiRequestParams,
			tailSegmentsConfiguration
		);

		this.setState({ data: groupSegments(converDataToTree(data)) });
	}

	fetchTaxonomies = () => {
		const { initiativeData } = this.state;
		const { spendType, initiativeItemType } = initiativeData;

		if (
			(initiativeItemType === ITEMS_TYPE.CATEGORY &&
				spendType === SPEND_PARAM.INDIRECT) ||
			initiativeItemType === ITEMS_TYPE.PT_HARMONIZATION ||
			initiativeItemType === ITEMS_TYPE.TAIL_SPEND
		) {
			let spendParam = '';
			if (initiativeItemType === ITEMS_TYPE.TAIL_SPEND) {
				spendParam = 'tailspend';
			} else {
				spendParam = spendType === SPEND_PARAM.INDIRECT ? 'indirect' : 'direct';
			}
			getProjectTaxonomies(spendParam).then(taxonomies => {
				this.setState({ taxonomies });
			});
		} else {
			this.setState({ taxonomies: [] });
		}
	};

	handleTailSpendChange = updates => {
		const { tailSpendConfig } = this.state;

		this.setState({
			tailSpendConfig: { ...tailSpendConfig, ...updates },
			data: null,
		});
	};

	handleSettingsCheckboxChange = () => {
		const { shouldUpdateSettings } = this.state;

		this.setState({ shouldUpdateSettings: !shouldUpdateSettings });
	};

	handleShowSegmentsChange = () => {
		const { showSegments } = this.state;

		this.setState({ showSegments: !showSegments });
	};

	spendFormatter = value => {
		const { currencySymbol } = this.props;

		return value
			? getFormattedSpendWithSymbol(value, currencySymbol).toUpperCase()
			: '-';
	};

	isSubmitDisabled = () => {
		const {
			isSaving,
			initiativeData,
			activeStep,
			tailSpendConfig,
		} = this.state;
		const {
			type,
			duration,
			executionStartDate,
			name,
			initiativeItemType,
			startDate,
			spendType,
			endDate,
			items,
		} = initiativeData;

		const isStep1SubmitDisabled =
			spendType === null ||
			!initiativeItemType ||
			!type ||
			!startDate ||
			!endDate;
		const isStep2SubmitDisabled = isStep1SubmitDisabled || items.length === 0;
		const isStep3SubmitDisabled =
			isStep2SubmitDisabled ||
			!name ||
			!executionStartDate ||
			!duration ||
			isSaving;
		const isStep4SubmitDisabled =
			!tailSpendConfig || !tailSpendConfig.tailSpend;

		switch (activeStep) {
			case 1:
				return isStep1SubmitDisabled;
			case 2:
				return isStep2SubmitDisabled;
			case 3:
				return isStep3SubmitDisabled;
			case 4:
				return isStep4SubmitDisabled;
			default:
				return isStep3SubmitDisabled;
		}
	};

	getStepsConfig = () => {
		const { initiativeData } = this.state;
		const { initiativeItemType } = initiativeData;

		return STEPS_CONFIG.filter(item => {
			return item.requiredType
				? item.requiredType === initiativeItemType
				: true;
		});
	};

	getDateRangeText() {
		const { initiativeData } = this.state;
		const { startDate, endDate } = initiativeData;

		const f = 'MMM YYYY';
		return `${moment(startDate).format(f)}–${moment(endDate).format(f)}`;
	}

	onShowTopVendorsModal = item => {
		const { showTopVendors } = this.props;
		const { initiativeData } = this.state;
		const {
			initiativeItemType,
			startDate,
			endDate,
			spendType,
			tailSegmentsConfiguration,
		} = initiativeData;
		const modalData = {
			initiativeItemType,
			dateFrom: startDate,
			dateTo: endDate,
			spendType,
			...(tailSegmentsConfiguration
				? {
						tailSpendValue: tailSegmentsConfiguration.tailSpendValue,
						tailSpendType: tailSegmentsConfiguration.tailSpendType,
				  }
				: {}),
		};

		showTopVendors(modalData, item);
	};
	render() {
		const { onClose, companies, currencySymbol, id } = this.props;
		const {
			activeStep,
			data,
			groupBy,
			filterCategory,
			filterPTCategory,
			taxonomies,
			defaultDateRange,
			dateRangeOptions,
			waves,
			initiativeData,
			isLoading,
			isCommonVendors,
			tailSpendConfig,
			shouldUpdateSettings,
			showSegments,
		} = this.state;
		const { initiativeItemType, startDate, endDate } = initiativeData;
		const nextStep = this.getNextStep();

		return (
			<StyledModal
				footer={() => (
					<ApButtonGroup alignItems="center">
						<ApButtonSecondary onClick={onClose}>Cancel</ApButtonSecondary>
						<ApButtonMain
							onClick={this.handleSubmit}
							disabled={this.isSubmitDisabled()}
						>
							{nextStep
								? id
									? 'Done'
									: 'Next'
								: `${id ? 'Save' : 'Create'} initiative`}
						</ApButtonMain>
					</ApButtonGroup>
				)}
				hasClosed={onClose}
				isOpen
			>
				<StepHeader
					activeStep={activeStep}
					options={this.getStepsConfig()}
					onStepClick={this.handleStepClick}
					isEdit={!!id}
				/>
				<Content>
					{activeStep === 1 ? (
						<InitiativeTypeForm
							defaultDateRange={defaultDateRange}
							dateRangeOptions={dateRangeOptions}
							initiativeData={initiativeData}
							onInitiativeChange={this.changeInitiative}
							onParamsChange={this.handleDataParamsChange}
						/>
					) : activeStep === 2 ? (
						initiativeItemType === ITEMS_TYPE.PT_IMPROVEMENT ||
						initiativeItemType === ITEMS_TYPE.PT_HARMONIZATION ? (
							<PTItemsStep
								isEdit={!!id}
								data={data}
								isCommonVendors={isCommonVendors}
								onCommonVendorsChange={this.toggleCommonVendors}
								companies={companies}
								spendFormatter={this.spendFormatter}
								initiativeData={initiativeData}
								onInitiativeChange={this.changeInitiative}
								taxonomies={taxonomies}
								filterPTCategory={filterPTCategory}
								onFilterCategoryChange={this.handleFilterPTCategoryChange}
								isLoading={isLoading}
								onWaccChange={this.handleWaccChange}
								showTopVendors={this.onShowTopVendorsModal}
							/>
						) : (
							<ItemsSelectionStep
								data={data}
								companies={companies}
								spendFormatter={this.spendFormatter}
								initiativeData={initiativeData}
								onInitiativeChange={this.changeInitiative}
								groupBy={groupBy}
								onGroupByChange={this.handleGroupByChange}
								categoryOptions={this.getCategoryOptions()}
								filterCategory={filterCategory}
								onFilterCategoryChange={this.handleFilterCategoryChange}
								isCommonVendors={isCommonVendors}
								onCommonVendorsChange={this.toggleCommonVendors}
								showSegments={showSegments}
								onShowSegmentsChange={this.handleShowSegmentsChange}
								showTopVendors={this.onShowTopVendorsModal}
							/>
						)
					) : activeStep === 3 ? (
						<ConfigureInitiative
							taxonomies={taxonomies}
							companies={companies}
							spendFormatter={this.spendFormatter}
							initiativeData={initiativeData}
							onInitiativeChange={this.changeInitiative}
							currencySymbol={currencySymbol}
							waves={waves}
							data={
								(initiativeItemType === ITEMS_TYPE.PT_HARMONIZATION
									? data
									: initiativeItemType === ITEMS_TYPE.TAIL_SPEND) &&
								data &&
								getLeaves(data)
							}
							onWaccChange={this.handleWaccChange}
							showTopVendors={this.onShowTopVendorsModal}
						/>
					) : activeStep === 4 ? (
						tailSpendConfig && (
							<ScrollableWrapper>
								<Title>
									Initiative tail spend settings
									<span>{this.getDateRangeText()}</span>
								</Title>
								<TailSpendSection
									onChange={this.handleTailSpendChange}
									value={tailSpendConfig}
									previewDateRange={{
										dateFrom: startDate,
										dateTo: endDate,
									}}
									suppressSegmentsDownload
									suppressVendorRecurrence
								/>
								<CheckboxWrapper>
									<ApCheckbox
										onChange={this.handleSettingsCheckboxChange}
										checked={shouldUpdateSettings}
									>
										<CheckboxLabel>
											Select to update the default settings with these
											initiative settings
										</CheckboxLabel>
									</ApCheckbox>
								</CheckboxWrapper>
							</ScrollableWrapper>
						)
					) : null}
				</Content>
			</StyledModal>
		);
	}
}

export default props => {
	const { currency } = useContext(QueryStringContext);
	const {
		options: { currency: currencyList },
	} = useContext(SettingsOptionsContext);
	const { settings } = useContext(SpendCubeSettingsContext);
	const { companiesList } = useContext(SettingsContext);
	const currencySymbol = getCurrencySymbolFromOptions(currencyList, currency);

	return (
		<InitiativeEditorModal
			{...props}
			currencySymbol={currencySymbol}
			currency={currency}
			companies={companiesList}
			settings={settings}
		/>
	);
};
